/**
 * RetroDifferePage — hub saison différé rétro (gameplay via Game.tsx / onglet Partie).
 * L'ordre des donnes est imposé par le backend : pas de choix utilisateur.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PlayerAvatar } from '../../components/PlayerAvatar';
import { useApp } from '../../contexts/AppContext';
import type { Session } from '../../types/app';
import {
  createSession,
  fetchCurrentSeasonForPlay,
  fetchSession,
  startDealFingerprint,
  type RetroLeaderboardEntry,
  type RetroPlayerSession,
  type RetroSeasonLeaderboard,
  type RetroSessionDeal,
  type LegacyParticipationInfo,
} from '../../lib/retroDiffereApi';
import '../../styles/differe-results.css';
import '../../styles/retro-differe.css';

interface RetroDifferePageProps {
  session: Session;
  onBack: () => void;
}

function formatRanking(session: RetroPlayerSession, hasAccess: boolean): string {
  if (session.status !== 'finished') return '';
  if (hasAccess && session.virtualRank != null && session.totalRankedParticipants != null) {
    return `Classement : ${session.virtualRank} / ${session.totalRankedParticipants}`;
  }
  if (session.decile != null) {
    return `Décile : ${session.decile} / 10`;
  }
  return 'Session terminée';
}

function findPlayableDeal(deals: RetroSessionDeal[]): RetroSessionDeal | null {
  return deals.find(d => d.playable) ?? null;
}

function formatPercentDelta(delta: number): string {
  const sign = delta > 0 ? '+' : '';
  return `${sign}${delta.toFixed(1)} pts`;
}

function PercentCell({ value, title }: { value: number; title?: string }) {
  if (!Number.isFinite(value) || value <= 0) {
    return <span className="differe-pct-empty" title={title}>—</span>;
  }
  const fixed = value.toFixed(1);
  const [int, dec] = fixed.split('.');
  const tone = value >= 60 ? 'good' : value >= 40 ? 'mid' : 'low';
  return (
    <span className={`differe-pct-cell tone-${tone}`} title={title}>
      <span className="differe-pct-bar-bg">
        <span
          className="differe-pct-bar-fill"
          style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
        />
      </span>
      <span className="differe-pct-cell-text">
        {int}<span className="differe-decimal">,{dec}</span>
        <span className="differe-pct-suffix">%</span>
      </span>
    </span>
  );
}

function LeaderboardMeta({ entry }: { entry: RetroLeaderboardEntry }) {
  if (entry.isReferenceBot) {
    const parts = [
      'référence IA',
      `${entry.playedDealCount}/${entry.totalDealCount} donnes historiques`,
    ];
    if (entry.legacyOfficialRank != null) {
      parts.push(`class. historique ${entry.legacyOfficialRank}`);
    }
    return (
      <span className="retro-differe-leaderboard-meta">
        {parts.join(' · ')}
      </span>
    );
  }

  const parts: string[] = [
    `${entry.virtualRank} / ${entry.totalParticipants} sur le différé`,
  ];
  if (entry.expectedRank != null && entry.playedDealCount < entry.totalDealCount) {
    parts.push(`class. espéré ${entry.expectedRank}`);
  }
  if (entry.legacyOfficialRank != null) {
    parts.push(`à l'époque ${entry.legacyOfficialRank}`);
  }
  if (entry.percentDelta != null) {
    parts.push(formatPercentDelta(entry.percentDelta));
  }
  return (
    <span className="retro-differe-leaderboard-meta">
      {parts.join(' · ')}
    </span>
  );
}

function LeaderboardRow({
  entry,
  isMe,
}: {
  entry: RetroLeaderboardEntry;
  isMe: boolean;
}) {
  return (
    <div className={`differe-results-row retro-differe-leaderboard-row ${isMe ? 'is-me' : ''} ${entry.isReferenceBot ? 'is-reference-bot' : ''}`}>
      <span className="differe-col-rank">
        {entry.retroRank <= 3
          ? <span className={`differe-medal rank-${entry.retroRank}`}>{entry.retroRank}</span>
          : entry.retroRank}
      </span>
      <div className="differe-col-player">
        <PlayerAvatar pseudo={entry.pseudo} size={28} />
        <div className="retro-differe-leaderboard-player">
          <span className="differe-player-name" title={entry.pseudo}>
            {entry.pseudo}
          </span>
          <LeaderboardMeta entry={entry} />
        </div>
      </div>
      <span className="differe-col-score">
        <PercentCell
          value={entry.averagePercent}
          title={`Moyenne rétro ${entry.averagePercent.toFixed(2)}% · ${entry.playedDealCount}/${entry.totalDealCount} donnes`}
        />
      </span>
    </div>
  );
}

function RetroLeaderboardPanel({
  leaderboard,
  viewerPseudo,
}: {
  leaderboard: RetroSeasonLeaderboard;
  viewerPseudo: string;
}) {
  const viewerKey = viewerPseudo.trim().toLowerCase();
  const viewerInTop3 = leaderboard.top3.some(
    e => e.pseudo.trim().toLowerCase() === viewerKey,
  );
  const showViewerSeparate = leaderboard.viewer != null && !viewerInTop3;
  const referenceBot = leaderboard.referenceBot;

  if (!referenceBot && leaderboard.rankedPlayerCount === 0) {
    return (
      <p className="retro-differe-leaderboard-empty">
        Aucun classement provisoire pour l&apos;instant — jouez une donne pour apparaître ici.
      </p>
    );
  }

  const humanCount = Math.max(0, leaderboard.rankedPlayerCount - (referenceBot ? 1 : 0));

  return (
    <section className="retro-differe-leaderboard">
      <h3>Classement rétro en cours</h3>
      <p className="retro-differe-leaderboard-hint">
        {humanCount > 0
          ? `${humanCount} joueur${humanCount > 1 ? 's' : ''} classé${humanCount > 1 ? 's' : ''} · moyenne sur les donnes jouées`
          : 'Jouez une donne pour entrer au classement'}
        {referenceBot ? ' · Bot1 : référence fixe (scores historiques)' : ''}
      </p>
      <div className="differe-results-table retro-differe-leaderboard-table">
        <div className="differe-results-header">
          <span className="differe-col-rank">#</span>
          <span className="differe-col-player">Joueur</span>
          <span className="differe-col-score">Score</span>
        </div>
        <div className="differe-results-body">
          {leaderboard.top3.map(entry => (
            <LeaderboardRow
              key={entry.pseudo}
              entry={entry}
              isMe={entry.pseudo.trim().toLowerCase() === viewerKey}
            />
          ))}
          {showViewerSeparate && leaderboard.viewer && (
            <>
              <div className="retro-differe-leaderboard-separator">Votre classement</div>
              <LeaderboardRow entry={leaderboard.viewer} isMe />
            </>
          )}
          {referenceBot && (
            <>
              <div className="retro-differe-leaderboard-separator">Référence · Bot1</div>
              <LeaderboardRow entry={referenceBot} isMe={false} />
            </>
          )}
        </div>
      </div>
    </section>
  );
}

export default function RetroDifferePage({ session, onBack }: RetroDifferePageProps) {
  const { startGame, gameInfo } = useApp();
  const [loading, setLoading] = useState(true);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [seasonId, setSeasonId] = useState<number | null>(null);
  const [seasonMeta, setSeasonMeta] = useState<{
    legacyType: string;
    startsAt: string;
    endsAt: string;
    dealCount: number;
  } | null>(null);
  const [playerSession, setPlayerSession] = useState<RetroPlayerSession | null>(null);
  const [deals, setDeals] = useState<RetroSessionDeal[]>([]);
  const [legacyParticipation, setLegacyParticipation] = useState<LegacyParticipationInfo | null>(null);
  const [leaderboard, setLeaderboard] = useState<RetroSeasonLeaderboard | null>(null);

  const playedCount = useMemo(
    () => deals.filter(d => d.finishedAt != null).length,
    [deals],
  );

  const playableDeal = useMemo(() => findPlayableDeal(deals), [deals]);

  const refreshHub = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const data = await fetchCurrentSeasonForPlay(session.pseudo);
      if (!data) {
        setSeasonMeta(null);
        setPlayerSession(null);
        setLegacyParticipation(null);
        setLeaderboard(null);
        setDeals([]);
        return;
      }
      setSeasonId(data.season.id);
      setSeasonMeta({
        legacyType: data.season.legacyType,
        startsAt: data.season.startsAt,
        endsAt: data.season.endsAt,
        dealCount: data.dealCount,
      });
      setLegacyParticipation(data.legacyParticipation ?? null);
      setLeaderboard(data.leaderboard ?? null);
      setPlayerSession(data.session);
      if (data.session) {
        const detail = await fetchSession(session.pseudo, data.session.id);
        setDeals(detail.deals);
        setPlayerSession(detail.session);
      } else {
        setDeals([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, [session.pseudo]);

  const prevGameInfoRef = useRef(gameInfo);

  useEffect(() => {
    void refreshHub();
  }, [refreshHub]);

  // Rafraîchir au retour de partie (gameInfo passe de défini à null), pas à chaque MAJ de session.
  useEffect(() => {
    const hadRetroGame = Boolean(prevGameInfoRef.current?.retroDiffere);
    if (hadRetroGame && !gameInfo) {
      void refreshHub({ silent: true });
    }
    prevGameInfoRef.current = gameInfo;
  }, [gameInfo, refreshHub]);

  const launchDeal = useCallback(async (
    sess: RetroPlayerSession,
    dealList: RetroSessionDeal[],
    currentSeasonId: number,
  ) => {
    const deal = findPlayableDeal(dealList);
    if (!deal) {
      setError('Aucune donne disponible. Terminez votre session si vous avez tout joué.');
      return;
    }
    setError(null);
    const rejoin = !!(deal.startedAt && !deal.finishedAt);
    if (!rejoin) {
      await startDealFingerprint(session.pseudo, currentSeasonId, deal.dealIndex);
    }
    startGame({
      idserv: 'diff_indiv',
      retroDiffere: {
        sessionId: sess.id,
        dealIndex: deal.dealIndex,
        seasonId: currentSeasonId,
        rejoin,
        totalDeals: dealList.length,
      },
    });
  }, [session.pseudo, startGame]);

  const handleStartOrResume = async () => {
    if (!seasonId) return;
    setLaunching(true);
    setError(null);
    try {
      const sess = playerSession ?? await createSession(session.pseudo, seasonId);
      const detail = await fetchSession(session.pseudo, sess.id);
      setPlayerSession(detail.session);
      setDeals(detail.deals);
      await launchDeal(detail.session, detail.deals, seasonId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de démarrer');
    } finally {
      setLaunching(false);
    }
  };

  const primaryLabel = useMemo(() => {
    if (launching) return 'Chargement…';
    if (playableDeal?.startedAt && !playableDeal.finishedAt) return 'Reprendre la donne en cours';
    return 'Jouer le différé';
  }, [launching, playableDeal]);

  return (
    <div className="retro-differe-hub">
      <button type="button" className="retro-differe-back" onClick={onBack}>← Liste des différés</button>
      <h2>Différé Rétro</h2>
      {loading && !seasonMeta && <p>Chargement…</p>}
      {error && <div className="retro-differe-error">{error}</div>}
      {!loading && !seasonMeta && (
        <p>Aucune saison publiée pour le moment.</p>
      )}
      {seasonMeta && (
        <>
          <p className="retro-differe-meta">
            Type : {seasonMeta.legacyType}<br />
            Du {new Date(seasonMeta.startsAt).toLocaleDateString('fr-FR')} au {new Date(seasonMeta.endsAt).toLocaleDateString('fr-FR')}<br />
            {seasonMeta.dealCount} donnes
          </p>
          {playerSession && playerSession.status !== 'finished' && (
            <p>Progression : {playedCount} / {seasonMeta.dealCount} donnes terminées</p>
          )}
          {playerSession?.status === 'finished' && (
            <p className="retro-differe-ranking">
              {formatRanking(playerSession, !!session.hasCompetitionAccess)}
            </p>
          )}
          {playerSession?.status !== 'finished' && (
            <>
              <button
                type="button"
                className="retro-differe-primary"
                disabled={loading || launching || !playableDeal}
                onClick={() => void handleStartOrResume()}
              >
                {primaryLabel}
              </button>
              {playerSession && !playableDeal && playedCount >= seasonMeta.dealCount && (
                <p className="retro-differe-meta">Toutes les donnes sont jouées.</p>
              )}
            </>
          )}
          {leaderboard && (
            <RetroLeaderboardPanel
              leaderboard={leaderboard}
              viewerPseudo={session.pseudo}
            />
          )}
          {legacyParticipation?.participated && (
            <p className="retro-differe-legacy-participation">
              Vous aviez participé à ce différé historique
              {legacyParticipation.officialRank != null && (
                <> — classement à l&apos;époque : {legacyParticipation.officialRank}</>
              )}
              {legacyParticipation.legacyDecile != null && (
                <> (décile {legacyParticipation.legacyDecile} / 10)</>
              )}
            </p>
          )}
          {legacyParticipation && !legacyParticipation.participated && (
            <p className="retro-differe-legacy-participation retro-differe-legacy-participation--none">
              Vous n&apos;aviez pas participé à ce différé historique à l&apos;époque.
            </p>
          )}
        </>
      )}
    </div>
  );
}
